/**
 * Cookie consent categories matching Termly's taxonomy
 *
 * Categories:
 * - essential: Always enabled, required for core functionality (auth, security)
 * - performance: Speed/optimization cookies (load balancing, CDN)
 * - analytics: Usage tracking (PostHog, Google Analytics)
 * - advertising: Ad targeting and retargeting
 * - socialNetworking: Social media sharing and embeds
 * - unclassified: Cookies not yet categorized
 */
export type CookieCategory =
  | 'essential'
  | 'performance'
  | 'analytics'
  | 'advertising'
  | 'socialNetworking'
  | 'unclassified'

/**
 * All cookie categories as an array (useful for iteration)
 */
export const COOKIE_CATEGORIES: CookieCategory[] = [
  'essential',
  'performance',
  'analytics',
  'advertising',
  'socialNetworking',
  'unclassified'
]

/**
 * Categories that are always enabled and cannot be toggled
 */
export const ALWAYS_ENABLED_CATEGORIES: CookieCategory[] = ['essential']

/**
 * Consent status for the banner interaction
 */
export type ConsentStatus = 'pending' | 'accepted' | 'rejected'

/**
 * How the consent was given
 */
export type ConsentMethod = 'banner' | 'settings' | 'api'

/**
 * Backend sync status
 */
export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error'

/**
 * Category consent state - maps each category to a boolean
 */
export interface CategoryConsent {
  /** Always true - core functionality */
  essential: boolean
  /** Speed/optimization cookies */
  performance: boolean
  /** PostHog tracking */
  analytics: boolean
  /** Ad targeting */
  advertising: boolean
  /** Social media cookies */
  socialNetworking: boolean
  /** Uncategorized cookies */
  unclassified: boolean
}

/**
 * Cookie consent state
 */
export interface CookieConsentState {
  /** Whether user has made a consent decision */
  hasConsented: boolean
  /** Overall consent status */
  status: ConsentStatus
  /** Individual category consent */
  categories: CategoryConsent
  /** ISO timestamp when consent was given/rejected */
  consentedAt: string | null
  /** Version of consent banner (for tracking changes) */
  consentVersion: string
  /** How the consent was given (if consented) */
  consentMethod: ConsentMethod | null
  /**
   * Internal flag to track if backend sync has occurred this session.
   * Shared across all hook instances to prevent race conditions on reset.
   * @internal
   */
  _hasSyncedFromBackend: boolean
}

/**
 * Backend sync state tracking
 */
export interface BackendSyncState {
  /** Current sync status */
  syncStatus: SyncStatus
  /** Last sync error message if any */
  syncError: string | null
  /** ISO timestamp of last successful sync */
  lastSyncedAt: string | null
  /** Whether backend is the source of truth (user is authenticated) */
  isBackendAuthoritative: boolean
}

/**
 * Cookie consent actions
 */
export interface CookieConsentActions {
  /** Accept all cookies */
  acceptAll: () => void
  /** Reject all non-essential cookies */
  rejectAll: () => void
  /** Accept specific categories */
  acceptCategories: (categories: Partial<CategoryConsent>) => void
  /** Reset consent (for testing/settings changes) */
  resetConsent: () => void
  /** Check if a specific category is consented */
  hasConsentFor: (category: CookieCategory) => boolean
}

/**
 * Backend sync actions (added by the public hook)
 */
export interface BackendSyncActions {
  /** Manually trigger sync to backend */
  syncToBackend: () => Promise<void>
  /** Refresh consent from backend */
  refreshFromBackend: () => Promise<void>
}

/**
 * Internal store interface (private - used by store file only)
 */
export interface CookieConsentStore extends CookieConsentState {
  /** Simple setters for internal use */
  setCategories: (categories: Partial<CategoryConsent>) => void
  setConsentState: (state: Partial<CookieConsentState>) => void
  reset: () => void
  /** Mark that backend sync has occurred (prevents re-sync on component mount) */
  setHasSyncedFromBackend: (value: boolean) => void
}

/**
 * Public hook interface (public API)
 * This is what components should use
 */
export interface UseCookieConsent extends CookieConsentState, CookieConsentActions, BackendSyncState, BackendSyncActions {}

/**
 * Current consent banner version
 * Increment this when consent requirements change significantly
 *
 * Version history:
 * - 1.0.0: Initial 4-category system (necessary, analytics, functionality, marketing)
 * - 2.0.0: Expanded to 6-category Termly system with backend sync
 */
export const CONSENT_VERSION = '2.0.0'

/**
 * Default category consent - all non-essential categories are off
 */
export const DEFAULT_CATEGORY_CONSENT: CategoryConsent = {
  essential: true,       // Always enabled
  performance: false,    // Off until consent
  analytics: false,      // Off until consent
  advertising: false,    // Off until consent
  socialNetworking: false, // Off until consent
  unclassified: false    // Off until consent
}

/**
 * All categories enabled consent state
 */
export const ALL_CATEGORIES_ENABLED: CategoryConsent = {
  essential: true,
  performance: true,
  analytics: true,
  advertising: true,
  socialNetworking: true,
  unclassified: true
}

/**
 * Migration helper: Convert old 4-category consent to new 6-category format
 *
 * Mapping:
 * - necessary -> essential
 * - functionality -> performance
 * - analytics -> analytics
 * - marketing -> advertising
 * - socialNetworking -> defaults to false (new)
 * - unclassified -> defaults to false (new)
 */
export function migrateFromLegacyCategories(
  legacyCategories: {
    necessary?: boolean
    analytics?: boolean
    functionality?: boolean
    marketing?: boolean
  }
): CategoryConsent {
  return {
    essential: legacyCategories.necessary ?? true,
    performance: legacyCategories.functionality ?? false,
    analytics: legacyCategories.analytics ?? false,
    advertising: legacyCategories.marketing ?? false,
    socialNetworking: false, // New category, default off
    unclassified: false      // New category, default off
  }
}

/**
 * Check if stored consent needs migration
 * Returns true if the stored data uses the old 4-category format
 */
export function needsMigration(
  storedCategories: Record<string, boolean>
): boolean {
  // Old format has 'necessary' key, new format has 'essential'
  return 'necessary' in storedCategories && !('essential' in storedCategories)
}
